import type { ApiError } from "@circle-react/config/CustomErrors";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { Comment } from "@circle-react/types/Comment";
import type { PaginationHelper } from "@circle-react/types/InternalAPIHelper";
import {
  type InfiniteData,
  infiniteQueryOptions,
  queryOptions,
} from "react-query";

export const commentKey = createQueryKeyFactory("comment");

const COMMENTS_PER_PAGE = 15;

const listKey = (postId: number, parentCommentId: number | null = null) =>
  commentKey.key("list", postId, parentCommentId ?? "");

type CommentListData = InfiniteData<PaginationHelper<Comment>>;

interface ListParams {
  parentCommentId?: number | null;
  postId: number;
}

interface HierarchyParams {
  id: number;
  postId: number;
}

// `gcTime: 0` drops the data on unmount; `staleTime: Infinity` makes in-tree
// mutations the source of truth while mounted (external invalidation is the only
// refetch) and keeps focus refetches from clobbering optimistic updates.
export const commentQueries = {
  list: ({ postId, parentCommentId = null }: ListParams) =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: listKey(postId, parentCommentId),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<PaginationHelper<Comment>>(
          internalApi.comments.index({
            postId,
            params: {
              per_page: COMMENTS_PER_PAGE,
              page: pageParam,
              ...(parentCommentId
                ? { parent_comment_id: parentCommentId }
                : {}),
            },
          }),
        ),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      gcTime: 0,
      staleTime: Infinity,
    }),
  hierarchy: ({ postId, id }: HierarchyParams) =>
    queryOptions<Comment, ApiError>({
      queryKey: commentKey.key("hierarchy", postId, id),
      queryFn: () =>
        reactQueryGet<Comment>(
          internalApi.comments.commentHierarchy({ postId, id }),
        ),
      retry: (failureCount, error) => error.status !== 404 && failureCount < 3,
      gcTime: 0,
      staleTime: Infinity,
    }),
};

export const commentRemovals = {
  forPost: ({ postId }: { postId: number }) => {
    queryClient.removeQueries({ queryKey: commentKey.key("list", postId) });
    queryClient.removeQueries({
      queryKey: commentKey.key("hierarchy", postId),
    });
  },
};

export const commentInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: commentKey.root() }),
};

// Top-level list records carry preview replies in `record.replies`; updates
// and removals must walk that field too, otherwise the preview drifts.
const applyToRecord = (
  record: Comment,
  id: number,
  updater: (comment: Comment) => Comment,
): Comment => {
  if (record.id === id) return updater(record);
  if (!record.replies?.some(reply => reply.id === id)) return record;
  return {
    ...record,
    replies: record.replies.map(reply =>
      reply.id === id ? updater(reply) : reply,
    ),
  };
};

const removeFromRecord = (record: Comment, id: number): Comment => {
  if (!record.replies?.some(reply => reply.id === id)) return record;
  return {
    ...record,
    replies: record.replies.filter(reply => reply.id !== id),
  };
};

// Because the queries above never refetch while mounted, mutation hooks must
// write through these helpers (not invalidate) so the `list` and `hierarchy`
// caches stay in sync without refetches clobbering optimistic state.
export const commentCache = {
  update: ({
    postId,
    id,
    updater,
  }: {
    id: number;
    postId: number;
    updater: (comment: Comment) => Comment;
  }) => {
    queryClient.setQueriesData<CommentListData>(
      { queryKey: commentKey.key("list", postId) },
      data =>
        data && {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            records: page.records.map(record =>
              applyToRecord(record, id, updater),
            ),
          })),
        },
    );
    queryClient.setQueriesData<Comment>(
      { queryKey: commentKey.key("hierarchy", postId) },
      data => (data ? applyToRecord(data, id, updater) : data),
    );
  },

  add: ({
    postId,
    parentCommentId = null,
    comment,
  }: {
    comment: Comment;
    parentCommentId?: number | null;
    postId: number;
  }) => {
    queryClient.setQueryData<CommentListData>(
      listKey(postId, parentCommentId),
      data => {
        if (!data || data.pages.length === 0) {
          return {
            pages: [
              {
                records: [comment],
                page: 1,
                has_next_page: false,
                count: 1,
                page_count: 1,
                per_page: COMMENTS_PER_PAGE,
              },
            ],
            pageParams: [undefined],
          };
        }
        const lastPageIndex = data.pages.length - 1;
        return {
          ...data,
          pages: data.pages.map((page, index) =>
            index === lastPageIndex
              ? { ...page, records: [...page.records, comment] }
              : page,
          ),
        };
      },
    );
    if (parentCommentId !== null) {
      commentCache.update({
        postId,
        id: parentCommentId,
        updater: current => ({
          ...current,
          replies_count: current.replies_count + 1,
        }),
      });
    }
  },

  remove: ({
    postId,
    id,
    parentCommentId = null,
  }: {
    id: number;
    parentCommentId?: number | null;
    postId: number;
  }) => {
    queryClient.setQueriesData<CommentListData>(
      { queryKey: commentKey.key("list", postId) },
      data =>
        data && {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            records: page.records
              .filter(record => record.id !== id)
              .map(record => removeFromRecord(record, id)),
          })),
        },
    );
    queryClient.setQueriesData<Comment | null>(
      { queryKey: commentKey.key("hierarchy", postId) },
      data => {
        if (!data) return data;
        // Null mirrors the 404 path so the view falls back to "not found".
        if (data.id === id) return null;
        return removeFromRecord(data, id);
      },
    );
    if (parentCommentId !== null) {
      commentCache.update({
        postId,
        id: parentCommentId,
        updater: current => ({
          ...current,
          replies_count: current.replies_count - 1,
        }),
      });
    }
  },

  setBookmark: ({
    postId,
    id,
    bookmarkId,
  }: {
    bookmarkId: number | null;
    id: number;
    postId: number;
  }) => {
    commentCache.update({
      postId,
      id,
      updater: comment => ({ ...comment, bookmark_id: bookmarkId }),
    });
  },

  moderationApprove: ({ postId, id }: { id: number; postId: number }) => {
    commentCache.update({
      postId,
      id,
      updater: comment => ({
        ...comment,
        flagged_for_approval_at: null,
        unresolved_flagged_reports_count: 0,
      }),
    });
  },

  moderationReject: ({
    postId,
    id,
    parentCommentId = null,
  }: {
    id: number;
    parentCommentId?: number | null;
    postId: number;
  }) => {
    commentCache.remove({ postId, id, parentCommentId });
  },
};
